import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "./api";
import type {
  Me,
  Org,
  Player,
  Game,
  Event,
  EventListStatus,
  EventRegistration,
  PublicEventRow,
  Tournament,
  TournamentFormat,
  BracketMatch,
  MasterLeaderboardRow,
  LeaderboardByRangePayload,
  LeaderboardRange,
  PlayerScoringStats,
  OrgScoringInsights,
  ChampionshipResult,
} from "./types";

// ---- auth/me
//
// A 401 from /api/me means "signed out" — not an error to surface in the UI.
// We swallow it and return null so callers see a clean signed-in/signed-out
// state.

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api<Me>("/me");
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
}

// ---- orgs

export function useCreateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      slug: string;
      timezone?: string;
    }) => api<Org>("/orgs", { method: "POST", body: input }),
    // Returning the invalidate promise blocks `mutateAsync` from resolving
    // until /me refetches with the new membership. Without this, callers
    // that redirect immediately after create see stale `orgs: []` and bounce
    // back into the no-org onboarding flow.
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

export type UpdateOrgInput = {
  name?: string;
  timezone?: string;
};

export function useCurrentOrg() {
  const { data: me } = useMe();
  const slug =
    typeof window !== "undefined"
      ? localStorage.getItem("activeOrgSlug")
      : null;
  const currentSlug =
    (slug && me?.orgs.find((o) => o.orgSlug === slug)?.orgSlug) ??
    me?.orgs[0]?.orgSlug ??
    null;
  return useQuery({
    queryKey: ["org", currentSlug],
    queryFn: () => api<Org>(`/orgs/${currentSlug}`, { public: true }),
    enabled: !!currentSlug,
  });
}

export function useUpdateOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateOrgInput) =>
      api<Org>("/orgs/current", { method: "PATCH", body: input }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["me"] }),
        qc.invalidateQueries({ queryKey: ["org"] }),
      ]);
    },
  });
}

export function useDeleteOrg() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>("/orgs/current", { method: "DELETE" }),
    onSuccess: async () => {
      // Clear cached me + org state so the UI doesn't keep referencing
      // a deleted row. /me refetches after.
      qc.removeQueries({ queryKey: ["org"] });
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// ---- players

export function useRoster() {
  return useQuery({
    queryKey: ["players"],
    queryFn: () => api<Player[]>("/players"),
  });
}

// Per-player offensive/defensive output from both-sides scoring.
// Enabled only once we have a player id; returns rounds=0 when the
// player has no both-sides games (the profile then hides the card).
export function usePlayerScoringStats(playerId: string | null | undefined) {
  return useQuery({
    queryKey: ["players", playerId, "scoring-stats"],
    enabled: !!playerId,
    queryFn: () =>
      api<PlayerScoringStats>(`/players/${playerId}/scoring-stats`),
    staleTime: 30_000,
  });
}

// Global app settings (platform-admin): commentary voice id + delivery.
export type CommentaryVoiceSettings = {
  speed?: number; // 0.7 .. 1.2
  stability?: number; // 0 .. 1
  similarityBoost?: number; // 0 .. 1
  style?: number; // 0 .. 1
  speakerBoost?: boolean;
};
// Mirrors DEFAULT_COMMENTARY_VOICE in packages/db (kept in sync by hand;
// the client bundle doesn't import the db package).
export const DEFAULT_COMMENTARY_VOICE: Required<CommentaryVoiceSettings> = {
  speed: 1.0,
  stability: 0.4,
  similarityBoost: 0.75,
  style: 0.5,
  speakerBoost: true,
};
export type AppSettings = {
  commentaryVoiceId?: string;
  commentaryVoice?: CommentaryVoiceSettings;
};

export function useAppSettings(enabled: boolean) {
  return useQuery({
    queryKey: ["admin", "settings"],
    enabled,
    queryFn: () => api<AppSettings>("/admin/settings"),
  });
}

export function usePatchAppSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: AppSettings) =>
      api<AppSettings>("/admin/settings", { method: "PATCH", body: patch }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "settings"] });
      qc.invalidateQueries({ queryKey: ["commentary", "config"] });
    },
  });
}

// Whether live AI commentary is available (server has Gemini +
// ElevenLabs + a voice id). Drives whether the Scorer shows the mic.
export function useCommentaryConfig() {
  return useQuery({
    queryKey: ["commentary", "config"],
    queryFn: () =>
      api<{ enabled: boolean; transcribe?: boolean }>("/commentary/config"),
    staleTime: 5 * 60_000,
  });
}

// Org-wide scoring analytics from both-sides games (offense/defense
// leaders, per-round averages, best round). rounds=0 → Insights hides
// the section.
export function useOrgScoringInsights() {
  return useQuery({
    queryKey: ["stats", "scoring"],
    queryFn: () => api<OrgScoringInsights>("/stats/scoring"),
    staleTime: 30_000,
  });
}

export function useCreatePlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      slug?: string;
      email?: string | null;
      location?: string | null;
    }) => api<Player>("/players", { method: "POST", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["players"] }),
  });
}

export function useUpdatePlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: {
        name?: string;
        slug?: string;
        email?: string | null;
        location?: string | null;
      };
    }) => api<Player>(`/players/${id}`, { method: "PATCH", body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["players"] }),
  });
}

export function useDeletePlayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/players/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["players"] }),
  });
}

// ---- me / profile

export function useUpdateMyProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { displayName?: string; slug?: string }) =>
      api<{
        id: string;
        slug: string;
        displayName: string;
        email: string | null;
        avatarUrl: string | null;
      }>("/me", { method: "PATCH", body: input }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["me"] });
    },
  });
}

// ---- platform admin (gated server-side)

export type AdminUserRow = {
  id: string;
  slug: string;
  displayName: string;
  email: string | null;
  avatarUrl: string | null;
  createdAt: string;
  isPlatformAdmin: boolean;
  orgCount: number;
};

export function useAdminUsers(search: string) {
  return useQuery({
    queryKey: ["admin", "users", search],
    queryFn: () => {
      const qs = search ? `?search=${encodeURIComponent(search)}` : "";
      return api<AdminUserRow[]>(`/admin/users${qs}`, { public: true });
    },
  });
}

export function useSetPlatformAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      isPlatformAdmin,
    }: {
      userId: string;
      isPlatformAdmin: boolean;
    }) =>
      api<{ userId: string; isPlatformAdmin: boolean }>(
        `/admin/users/${userId}`,
        { method: "PATCH", body: { isPlatformAdmin } },
      ),
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: ["admin", "users"] }),
        qc.invalidateQueries({ queryKey: ["me"] }),
      ]),
  });
}

// ---- games

export function useGames(
  opts: { eventId?: string; playerId?: string; virtual?: boolean } = {},
) {
  const params = new URLSearchParams();
  if (opts.eventId) params.set("eventId", opts.eventId);
  if (opts.playerId) params.set("playerId", opts.playerId);
  if (opts.virtual) params.set("virtual", "1");
  const qs = params.toString();
  return useQuery({
    queryKey: ["games", opts],
    queryFn: () => api<Game[]>(`/games${qs ? `?${qs}` : ""}`),
  });
}

// Active games where the signed-in user is on the roster AND the
// game hasn't completed yet (status live or scheduled). Drives the
// "Resume" banner on the dashboard.
//
// Cross-org: backed by /api/me/active-games which authorizes by
// user id and walks every player record the user has across every
// org. So a friend in a different org creating a game with you
// surfaces here without you needing to switch org contexts.
//
// Polled every 15s while focused so a freshly-created invite shows
// up without requiring a manual reload. Push notifications cover
// the "app is closed / not in focus" case.
export function useMyActiveGames(userId: string | null) {
  return useQuery({
    queryKey: ["me", "active-games", userId],
    queryFn: () => api<Game[]>(`/me/active-games`),
    enabled: !!userId,
    refetchInterval: 15_000,
    staleTime: 5_000,
  });
}

export function useGame(id: string | null) {
  return useQuery({
    queryKey: ["game", id],
    queryFn: () => api<Game>(`/games/${id}`),
    enabled: !!id,
  });
}

export function useGameByShareCode(code: string | null) {
  return useQuery({
    queryKey: ["g", code],
    queryFn: () => api<Game>(`/g/${code}`, { public: true }),
    enabled: !!code,
  });
}

export function useCreateGame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: unknown) =>
      api<Game>("/games", { method: "POST", body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["games"] });
      qc.invalidateQueries({ queryKey: ["leaderboards", "master"] });
    },
  });
}

// A round submission: either the hand-cancelled net (scoringTeam +
// netPoints) or both sides' gross (team1Gross + team2Gross, from which
// the server derives the net). Shared by add + edit.
export type FrameInput = {
  scoringTeam?: 1 | 2 | null;
  netPoints?: number;
  team1Gross?: number;
  team2Gross?: number;
};

export function useAddFrame(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FrameInput) =>
      api<{ game: Game; frame: unknown }>(`/games/${gameId}/frames`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["game", gameId] });
      // A frame may have completed the game, which enqueues a stats
      // job on the server. Invalidate the by-range cache + the
      // pending counter so the dashboard's "Updating stats…" badge
      // wakes up and the leaderboard refetches once the worker
      // finishes (its computedAt timestamp will be fresher).
      qc.invalidateQueries({ queryKey: ["leaderboards"] });
      qc.invalidateQueries({ queryKey: ["stats", "pending"] });
    },
  });
}

// Best-of-3 championship status for a game. Only fetch once the game
// is complete (enabled) — clinched=true drives the trophy salute.
export function useChampionship(
  gameId: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["game", gameId, "championship"],
    enabled: !!gameId && enabled,
    queryFn: () => api<ChampionshipResult>(`/games/${gameId}/championship`),
    staleTime: 15_000,
  });
}

export function useUndoFrame(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<Game>(`/games/${gameId}/undo`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["game", gameId] });
      qc.invalidateQueries({ queryKey: ["g"] });
    },
  });
}

// Roster pronouns: auto-set from names where confident, and surface anyone
// ambiguous so the runner can clarify. Plain calls (not hooks) — the Scorer
// fires them imperatively on game load / on the runner's answers.
export type RosterSex = "male" | "female" | "nonbinary" | "unspecified";

export function resolveRosterPronouns(
  gameId: string,
): Promise<{ needsClarification: { id: string; name: string }[] }> {
  return api(`/games/${gameId}/pronouns/resolve`, { method: "POST" });
}

export function applyRosterPronouns(
  gameId: string,
  answers: { playerId: string; sex: RosterSex }[],
): Promise<{ updated: number }> {
  return api(`/games/${gameId}/pronouns/apply`, {
    method: "POST",
    body: { answers },
  });
}

export function useDeleteGame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/games/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["games"] });
      qc.invalidateQueries({ queryKey: ["leaderboards"] });
    },
  });
}

// Cancel a game in progress — marks it status=voided on the server
// so it drops out of every player's active-games banner. Different
// from useDeleteGame (which hard-deletes): a voided game stays in
// history with status="voided", so post-mortems / "I cancelled
// this one" reviews still surface it.
export function useVoidGame() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<Game>(`/games/${id}`, {
        method: "PATCH",
        body: { status: "voided" },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["games"] });
      qc.invalidateQueries({ queryKey: ["leaderboards"] });
    },
  });
}

export function useUpdateFrame(gameId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      frameId,
      input,
    }: {
      frameId: string;
      input: FrameInput;
    }) =>
      api<{ game: Game; frame: unknown }>(
        `/games/${gameId}/frames/${frameId}`,
        { method: "PATCH", body: input },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["game", gameId] });
      qc.invalidateQueries({ queryKey: ["g"] });
    },
  });
}

// ---- events

export function useEvents(opts: { status?: EventListStatus } = {}) {
  const status = opts.status ?? "all";
  return useQuery({
    queryKey: ["events", status],
    queryFn: () => api<Event[]>(`/events?status=${status}`),
  });
}

export function useEvent(orgSlug: string | null, eventSlug: string | null) {
  return useQuery({
    queryKey: ["event", orgSlug, eventSlug],
    queryFn: () =>
      api<Event & { registrations: EventRegistration[] }>(
        `/orgs/${orgSlug}/events/${eventSlug}`,
        { public: true },
      ),
    enabled: !!orgSlug && !!eventSlug,
  });
}

export function useEventRegistrations(eventId: string | null) {
  return useQuery({
    queryKey: ["event", eventId, "registrations"],
    queryFn: () =>
      api<EventRegistration[]>(`/events/${eventId}/registrations`, {
        public: true,
      }),
    enabled: !!eventId,
  });
}

export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      slug?: string;
      description?: string | null;
      startDate: string;
      endDate?: string | null;
      location?: string | null;
      format: "singles" | "doubles" | "mixed";
      isPublic?: boolean;
      latitude?: number | null;
      longitude?: number | null;
    }) => api<Event>("/events", { method: "POST", body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export function useUpdateEvent(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<{
      name: string;
      description: string | null;
      startDate: string;
      endDate: string | null;
      location: string | null;
      format: "singles" | "doubles" | "mixed";
      isPublic: boolean;
      latitude: number | null;
      longitude: number | null;
    }>) => api<Event>(`/events/${id}`, { method: "PATCH", body: input }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
      qc.invalidateQueries({ queryKey: ["event"] });
    },
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/events/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export function useRegisterEntrant(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      playerId?: string;
      teamId?: string;
      seed?: string | null;
    }) =>
      api<EventRegistration>(`/events/${eventId}/registrations`, {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event", eventId, "registrations"] });
      qc.invalidateQueries({ queryKey: ["event"] });
    },
  });
}

export function useUnregisterEntrant(eventId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (registrationId: string) =>
      api<void>(`/events/${eventId}/registrations/${registrationId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["event", eventId, "registrations"] });
      qc.invalidateQueries({ queryKey: ["event"] });
    },
  });
}

// ---- tournaments

export function useEventTournaments(eventId: string | null) {
  return useQuery({
    queryKey: ["event", eventId, "tournaments"],
    queryFn: () =>
      api<Tournament[]>(`/events/${eventId}/tournaments`, { public: true }),
    enabled: !!eventId,
  });
}

export function useTournament(
  orgSlug: string | null,
  eventSlug: string | null,
  tournamentSlug: string | null,
) {
  return useQuery({
    queryKey: ["tournament", orgSlug, eventSlug, tournamentSlug],
    queryFn: () =>
      api<Tournament & { matches: BracketMatch[] }>(
        `/orgs/${orgSlug}/events/${eventSlug}/t/${tournamentSlug}`,
        { public: true },
      ),
    enabled: !!orgSlug && !!eventSlug && !!tournamentSlug,
  });
}

export function useTournamentMatches(tournamentId: string | null) {
  return useQuery({
    queryKey: ["tournament", tournamentId, "matches"],
    queryFn: () =>
      api<BracketMatch[]>(`/tournaments/${tournamentId}/bracket`, {
        public: true,
      }),
    enabled: !!tournamentId,
  });
}

export function useCreateTournament() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      eventId: string;
      name: string;
      format: TournamentFormat;
      rules?: { targetScore?: number };
    }) => api<Tournament>("/tournaments", { method: "POST", body: input }),
    onSuccess: (_t, vars) => {
      qc.invalidateQueries({ queryKey: ["event", vars.eventId, "tournaments"] });
      qc.invalidateQueries({ queryKey: ["event"] });
    },
  });
}

export function useSetMatchBoard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { matchId: string; boardNumber: number | null }) =>
      api<BracketMatch>(`/bracket-matches/${input.matchId}/board`, {
        method: "PATCH",
        body: { boardNumber: input.boardNumber },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tournament"] });
    },
  });
}

export function useSeedTournament(tournamentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<{ tournament: Tournament; matches: BracketMatch[] }>(
        `/tournaments/${tournamentId}/seed`,
        { method: "POST", body: {} },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["tournament"] });
    },
  });
}

// ---- public events listing (cross-org)

export type PublicEventsFilter = {
  q?: string;
  from?: string;
  to?: string;
  format?: "singles" | "doubles" | "mixed";
  near?: { lat: number; lng: number; radius: number };
};

export function usePublicEvents(filter: PublicEventsFilter = {}) {
  const params = new URLSearchParams();
  if (filter.q) params.set("q", filter.q);
  if (filter.from) params.set("from", filter.from);
  if (filter.to) params.set("to", filter.to);
  if (filter.format) params.set("format", filter.format);
  if (filter.near) {
    params.set("lat", String(filter.near.lat));
    params.set("lng", String(filter.near.lng));
    params.set("radius", String(filter.near.radius));
  }
  const qs = params.toString();
  return useQuery({
    queryKey: ["events-public", qs],
    queryFn: () =>
      api<PublicEventRow[]>(`/events-public${qs ? `?${qs}` : ""}`, {
        public: true,
      }),
  });
}

// ---- org public snapshot

export type OrgPublicSnapshot = {
  org: {
    id: string;
    slug: string;
    name: string;
    brandConfig: {
      primaryColor?: string;
      logoUrl?: string;
      tagline?: string;
    } | null;
    timezone: string;
  };
  upcomingEvents: Array<{
    id: string;
    slug: string;
    name: string;
    startDate: string;
    endDate: string | null;
    location: string | null;
    format: "singles" | "doubles" | "mixed";
  }>;
  recentGames: Array<{
    id: string;
    shareCode: string;
    playedOn: string;
    format: "singles" | "doubles";
    team1Score: number;
    team2Score: number;
    winningTeam: 1 | 2 | null;
    completedAt: string | null;
    team1Players: Array<{ id: string; name: string | null; slug: string | null }>;
    team2Players: Array<{ id: string; name: string | null; slug: string | null }>;
  }>;
  leaderboard: Array<{
    rank: number;
    playerId: string;
    name: string;
    slug: string;
    gamesPlayed: number;
    wins: number;
    losses: number;
    winPct: number;
  }>;
};

export function useOrgPublic(slug: string | null) {
  return useQuery({
    queryKey: ["org-public", slug],
    queryFn: () =>
      api<OrgPublicSnapshot>(`/orgs/${slug}/public`, { public: true }),
    enabled: !!slug,
  });
}

// ---- leaderboards

export function useMasterLeaderboard(opts: { scope?: "all" | "singles" | "doubles" } = {}) {
  const params = new URLSearchParams();
  if (opts.scope) params.set("scope", opts.scope);
  const qs = params.toString();
  return useQuery({
    queryKey: ["leaderboards", "master", opts],
    queryFn: () =>
      api<MasterLeaderboardRow[]>(`/leaderboards/master${qs ? `?${qs}` : ""}`),
  });
}

// Cached + range-filtered leaderboard. Backed by the stats pipeline
// (queue → worker → DB cache → in-process LRU on the API). Reads
// here are O(1) on the API cache-hit path. A cold miss returns a
// `computing` placeholder immediately (no inline aggregation) while
// the worker warms the row; we poll until it clears. The 30s React
// Query staleTime de-dupes a rapid tab-switch/refetch flurry without
// holding stale data through a game-complete event.
export function useLeaderboardByRange(range: LeaderboardRange) {
  return useQuery({
    queryKey: ["leaderboards", "by-range", range],
    queryFn: () =>
      api<LeaderboardByRangePayload>(`/leaderboards/by-range?range=${range}`),
    staleTime: 30_000,
    // While the cache is warming, poll every 1.5s until the payload
    // stops reporting `computing`; then fall idle (steady state is
    // governed by staleTime + game-complete invalidation).
    refetchInterval: (q) =>
      (q.state.data as LeaderboardByRangePayload | undefined)?.computing
        ? 1500
        : false,
  });
}

// Polls the org's pending stats-job count. The dashboard uses this
// to show a small "Updating stats…" badge while the worker is
// behind. Stops polling (refetchInterval=false) once the count
// hits zero so we don't keep a 1Hz timer running indefinitely;
// next game-complete will re-trigger via invalidation in
// useAddFrame's onSuccess.
export function useStatsPending() {
  return useQuery({
    queryKey: ["stats", "pending"],
    queryFn: () => api<{ pending: number }>("/stats/pending"),
    // Poll every 2s WHILE there are pending jobs; idle otherwise.
    refetchInterval: (q) =>
      (q.state.data as { pending: number } | undefined)?.pending ? 2000 : false,
    staleTime: 2_000,
  });
}

// ---- assets (visual catalog)

export type AssetKind = "scene" | "board" | "bag" | "sfx" | "music";
export type AssetStatus = "draft" | "active" | "inactive";

// Named sound-effect slots the game can fire. Mirrors SFX_SLOTS in
// the DB package — duplicated here so the FE doesn't take a server
// dependency on the schema package at runtime.
export const SFX_SLOTS = [
  "round_start",
  "round_winner",
  "throw_release",
  "bag_land",
  "bag_in_hole",
  "bag_on_ground",
  // Fires when a side puts up 12 in a round (all 4 bags in the hole).
  "four_bagger",
  "game_win",
] as const;
export type SfxSlot = (typeof SFX_SLOTS)[number];

export type Asset = {
  id: string;
  kind: AssetKind;
  slug: string | null;
  name: string;
  description: string | null;
  prompt: string | null;
  imageUrl: string | null;
  swatchHex: number | null;
  metadata: Record<string, unknown>;
  status: AssetStatus;
  isBuiltIn: boolean;
  createdAt: string;
  updatedAt: string;
};

// Public catalog read — used by the in-game picker UI. Returns only
// active rows (server-side filter). No auth required.
export function useAssetCatalog(kind?: AssetKind) {
  return useQuery({
    queryKey: ["assets", "catalog", kind ?? "all"],
    queryFn: () =>
      api<Asset[]>(`/assets${kind ? `?kind=${kind}` : ""}`),
    staleTime: 60_000,
  });
}

// Admin-side: every status, with optional filter. Used by /admin/design.
export function useAdminAssets(filter: {
  kind?: AssetKind;
  status?: AssetStatus | "all";
}) {
  const params = new URLSearchParams();
  if (filter.kind) params.set("kind", filter.kind);
  if (filter.status && filter.status !== "all") params.set("status", filter.status);
  const qs = params.toString();
  return useQuery({
    queryKey: ["assets", "admin", filter],
    queryFn: () => api<Asset[]>(`/admin/assets${qs ? `?${qs}` : ""}`),
  });
}

export function useAssetStatus() {
  return useQuery({
    queryKey: ["assets", "status"],
    queryFn: () =>
      api<{ hasGeminiKey: boolean; hasElevenLabsKey: boolean; hasR2: boolean }>(
        "/admin/assets/status",
      ),
  });
}

export function useGenerateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: AssetKind; prompt: string; name: string }) =>
      api<Asset>("/admin/assets/generate", { method: "POST", body: input }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

// Text → sound effect via ElevenLabs. Returns a draft sfx asset.
export function useGenerateSfx() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      prompt: string;
      name: string;
      durationSeconds?: number;
    }) =>
      api<Asset>("/admin/assets/generate-sfx", {
        method: "POST",
        body: input,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

// Upload an admin-prepared image (Photoshopped, removebg'd, etc.).
// Server expects a data-URL inside the JSON body — the FE converts
// the File via FileReader before calling.
export function useUploadAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      kind: AssetKind;
      name: string;
      dataUrl: string;
    }) =>
      api<Asset>("/admin/assets/upload", { method: "POST", body: input }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

export function useUpdateAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      name?: string;
      description?: string | null;
      status?: AssetStatus;
      // SFX rebinding — null clears the slot binding. Built-in rows
      // are server-protected; the route will 400 those.
      slug?: string | null;
      // Shallow-merged into the server-side metadata blob. Used by
      // per-asset alignment tools (bag texture aligner, etc.).
      metadata?: Record<string, unknown>;
    }) => {
      const { id, ...patch } = input;
      return api<Asset>(`/admin/assets/${id}`, { method: "PATCH", body: patch });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/admin/assets/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["assets"] });
    },
  });
}

// ---- physics tuning (global, single-row blob)
//
// Replaces the older per-device localStorage `cornholio.physicsTuning`
// store. The GET is intentionally public so the 3D arena can boot
// without auth (it needs gravity/aim/lighting before sign-in). The
// PATCH is admin-only.
//
// staleTime is short (30s) so an admin tuning on desktop sees those
// values land on mobile within half a minute of switching focus.

export type PhysicsTuningBlob = Record<string, unknown>;

export function usePhysicsTuningQuery() {
  return useQuery<PhysicsTuningBlob>({
    queryKey: ["physics-tuning"],
    queryFn: () => api<PhysicsTuningBlob>("/physics-tuning", { public: true }),
    staleTime: 30_000,
  });
}

export function useUpdatePhysicsTuning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PhysicsTuningBlob) =>
      api<PhysicsTuningBlob>("/admin/physics-tuning", {
        method: "PATCH",
        body: patch,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["physics-tuning"] });
    },
  });
}
